/**
 * intercept cart GET requests to add bundle enforcer logic in non-desctuctive way
 * does not alter requests, only gets the data to check for bundle rules and clean if needed
 */
(function () {
    /** @typedef {{ _selected_location: string, cart_token: string }} CartAttributes */

    /**
     * @typedef {{
     *   _sw_main_product_sku?: string,
     *   _sw_main_product_handle?: string,
     *   _uuid?: string,
     *   _set_number?: string,
     *   _tradeInEligible?: string,
     *   _secWarranty_sku?: string,
     *   _secWarranty_handle?: string,
     *   _aplcr_main_product_sku?: string,
     *   _aplcr_main_product_handle?: string,
     *   _isTradeInProduct?: string,
     *   _trade_in_set_number?: string,
     *   _isBundleProduct?: string,
     *   _bundle_set_number?: string
     * }} CartItemProperties
     */

    /**
     * @typedef {{
     *   id: number,
     *   properties: CartItemProperties,
     *   quantity: number,
     *   variant_id: number,
     *   key: string,
     *   product_id: number,
     *   handle: string,
     *   sku: string,
     *   product_type: string,
     *   product_title: string
     * }} CartItem
     */

    /**
     * @typedef {{
     *   token: string,
     *   note: string,
     *   attributes: CartAttributes,
     *   items: CartItem[]
     * }} Cart
    */

    if (typeof window === 'undefined') return;

    const originalFetch = window.fetch
    patchFetch();

    let ensureRule_debounce;

    /** warp the fetch in a "middleware" and get the cart data to check for bundle rules and clean if needed */
    function patchFetch() {
        window.fetch = async function (...args) {
            clearTimeout(ensureRule_debounce);
            const [url, options] = args;
            const { method } = options || {};

            if (isShopifyCartChange(url, method || "GET"))
                window.blockCheckoutUIandAddLoading && blockCheckoutUIandAddLoading();

            const response = await originalFetch(...args);

            // run without awaiting, excluding the response from the original fetch promise chain
            (async () => {
                if (isShopifyCartGet(url, method || "GET") && response.ok) {
                    // debounce — validate against a fresh cart.js, not the snapshot from mid-mutation GETs
                    clearTimeout(ensureRule_debounce);
                    ensureRule_debounce = setTimeout(async () => {
                        if (window.wdSuppressBundleEnforcer) return;
                        try {
                            // note using true it will fetch new data ands possibly prevent using stale data
                            const useNewFetchData = false;
                            let cart;
                            if (useNewFetchData) {
                                const freshRes = await originalFetch(`${window.Shopify.routes.root}cart.js`);
                                if (!freshRes.ok || window.wdSuppressBundleEnforcer) return;
                                cart = await freshRes.json();
                            }
                            else {
                                cart = await response.clone().json();
                            }
                            ensureBundleRules(cart);
                        } catch (_) {
                            // ignore
                        }
                    }, 3000);
                }
            })()


            return response;
        };
    }

    /** True for GET to storefront cart JSON (cart.js), not cart/add.js etc. */
    function isShopifyCartGet(url, method) {
        if (method.toUpperCase() !== 'GET') return false;
        const u = typeof url === 'string' ? url : '';
        return u === '/cart.js' || u.endsWith('/cart.js') || u.includes('/cart.js?');
    }

    /** True for POST to storefront cart JSON (cart.js), not cart/add.js etc. */
    function isShopifyCartChange(url, method) {
        if (method.toUpperCase() !== 'POST') return false;
        const u = typeof url === 'string' ? url : '';
        return u === '/update.js' || u.endsWith('/update.js') || u.includes('/update.js?') ||
            u === '/add.js' || u.endsWith('/add.js') || u.includes('/add.js?') ||
            u === '/change.js' || u.endsWith('/change.js') || u.includes('/change.js?');
    }

    /**
     * Validates trade-in and care add-on lines against the current cart (no extra fetch).
     * Logs warnings for orphans or missing link data.
     * @param {Cart} cart
     */
    function ensureBundleRules(cart) {
        if (window.wdSuppressBundleEnforcer) return;

        // get items
        const items = cart?.items;
        if (!items?.length) return;

        // get all invalid tradeins
        const issues = validateTradeIns(items);
        // get all invalid care attachments
        issues.push(...validateCareAttachments(items));
        // get all invalid bundle attachments
        issues.push(...validateBundleProducts(items));

        // if no issues, return
        if (!issues.length) return;

        // if we got issues, log them
        console.warn('bundle link issues', issues);

        const payload = { updates: {} };
        issues.forEach(issue => {
            payload.updates[issue.lineKey] = 0;
        });
        if (window.wdSuppressBundleEnforcer) return;

        fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
        }).then((res) => {
            if (!res.ok) {
                if (res.status === 409) {
                    console.warn('bundle enforcer: cart/update.js conflict (409), skipping retry');
                }
                return;
            }
            window.onCartUpdate?.();
        });
    }

    /** ensure all trade-in items have a linked parent item
     * @param {CartItem[]} items
     */
    function validateTradeIns(items) {
        /** @type {{ lineKey: string, reason: string, detail: string }[]} */
        const to_remove = [];

        // get all trading items
        items.filter(item => item.properties?._isTradeInProduct === 'true')
            .forEach(item => {
                const attachedTo = item.properties._trade_in_set_number;
                // if no attachedTo, add to remove
                if (!attachedTo) {
                    to_remove.push({ lineKey: item.key, reason: 'missing_trade_in_set_number', detail: `sku=${item.sku}` });
                    return;
                }
                // search for a non-trade-in item with the same _trade_in_set_number
                const hasLinkedParentItem = items.some(
                    (line) =>
                        line.key != item.key && // ignore self
                        line.properties?._trade_in_set_number == attachedTo && //matching set
                        line.properties?._isTradeInProduct != 'true' // not a trade-in
                );
                if (!hasLinkedParentItem)
                    to_remove.push({
                        lineKey: item.key,
                        reason: 'orphan_no_main_for_set',
                        detail: `sku=${item.sku} set=${attachedTo}`,
                    });
            });
        return to_remove;
    }

    /**
     * ensure all care items have a linked parent item
     * @param {CartItem[]} items
     * @returns 
     */
    function validateCareAttachments(items) {
        /** @type {{ lineKey: string, reason: string, detail: string }[]} */
        const to_remove = [];

        // filter for care items
        items.filter(item => item.properties?._sw_main_product_sku || item.properties?._aplcr_main_product_sku)
            .forEach(item => {
                const p = item.properties || {};
                const mainSku = p._sw_main_product_sku || p._aplcr_main_product_sku;

                const attachedTo = p._set_number;
                if (!attachedTo) {
                    to_remove.push({ lineKey: item.key, reason: 'missing_set_number_on_care_line', detail: `sku=${mainSku}` });
                    return;
                }
                // search for a non-care item with the same _set_number
                const hasLinkedParentItem = items.some((line) =>
                    line.key != item.key && // ignore self
                    line.properties?._set_number == attachedTo // matching set
                );
                if (!hasLinkedParentItem)
                    to_remove.push({ lineKey: item.key, reason: 'orphan_no_matching_main', detail: `sku=${mainSku} set=${attachedTo}` });
            });
        return to_remove;
    }

    /** ensure all bundle items have a linked parent item
     * @param {CartItem[]} items
     */
    function validateBundleProducts(items) {
        /** @type {{ lineKey: string, reason: string, detail: string }[]} */
        const to_remove = [];

        items.filter(item => item.properties?._isBundleProduct === 'true')
            .forEach(item => {
                const attachedTo = item.properties._bundle_set_number;
                if (!attachedTo) {
                    to_remove.push({ lineKey: item.key, reason: 'missing_bundle_set_number', detail: `sku=${item.sku}` });
                    return;
                }
                const hasLinkedParentItem = items.some(
                    (line) =>
                        line.key != item.key &&
                        line.properties?._bundle_set_number == attachedTo &&
                        line.properties?._isBundleProduct != 'true'
                );
                if (!hasLinkedParentItem)
                    to_remove.push({
                        lineKey: item.key,
                        reason: 'orphan_no_main_for_bundle_set',
                        detail: `sku=${item.sku} set=${attachedTo}`,
                    });
            });
        return to_remove;
    }
})();
